import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCheck, FiX, FiClock, FiAlertCircle } from 'react-icons/fi';
import { AVATARS } from '../constants/avatars';
import api from '../services/api';

const AttendanceStatus = {
  'NOT TAKEN': { icon: FiAlertCircle, color: 'text-gray-400', bg: 'bg-gray-100', text: 'Not Taken' },
  'PRESENT': { icon: FiCheck, color: 'text-green-500', bg: 'bg-green-50', text: 'Present' },
  'LATE': { icon: FiClock, color: 'text-yellow-500', bg: 'bg-yellow-50', text: 'Late' },
  'E ABSENT': { icon: FiX, color: 'text-orange-500', bg: 'bg-orange-50', text: 'Excused' },
  'ABSENT': { icon: FiX, color: 'text-red-500', bg: 'bg-red-50', text: 'Absent' }
};

export default function Attendance() {
  const { t } = useTranslation();
  const [attendanceData, setAttendanceData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        const response = await api.get('/attendance');
        setAttendanceData(response.data.data.attendance);
      } catch (err) {
        setError('Failed to load attendance information');
        console.error('Failed to fetch attendance:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAttendance();
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white px-4">
          {t('attendance')}
        </h2>
        <div className="grid gap-4">
          {[1, 2].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
              <div className="p-4">
                <div className="animate-pulse flex items-center space-x-4">
                  <div className="h-12 w-12 bg-gray-200 dark:bg-gray-700 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-3 gap-px bg-gray-200 dark:bg-gray-700">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="p-4 bg-white dark:bg-gray-800">
                      <div className="animate-pulse space-y-2">
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="text-red-600 dark:text-red-400">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white px-4">
        {t('attendance')}
      </h2>

      <div className="grid gap-4">
        {attendanceData.map((student) => (
          <div 
            key={student.student_name}
            className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden"
          >
            <div className="p-4 flex items-center space-x-4">
              <img
                src={student.student_gender === 'Male' ? AVATARS.MALE : AVATARS.FEMALE}
                alt=""
                className="h-12 w-12 rounded-full"
              />
              <span className="font-medium text-gray-900 dark:text-white">
                {student.student_name}
              </span>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-3 divide-x divide-gray-200 dark:divide-gray-700">
                {student.attendance.map((record, index) => {
                  const status = AttendanceStatus[record.attendance];
                  const StatusIcon = status.icon;
                  
                  return (
                    <div 
                      key={index}
                      className="p-4 text-center"
                    >
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        {record.date}
                      </div>
                      <div className={`inline-flex items-center px-2.5 py-1 rounded-full ${status.bg} dark:bg-opacity-10`}>
                        <StatusIcon className={`h-4 w-4 ${status.color} mr-1`} />
                        <span className={`text-sm ${status.color}`}>
                          {status.text}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}